import { useState, useEffect, useRef, useCallback } from "react";
import { FiSearch, FiX } from "react-icons/fi";
import styles from "./GifPicker.module.css";

const GIPHY_KEY = process.env.REACT_APP_GIPHY_API_KEY || "";
const LIMIT = 18;

const GifPicker = ({ onSelect, onClose, triggerRef }) => {
  const [query, setQuery]     = useState("");
  const [gifs, setGifs]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const containerRef          = useRef(null);
  const searchTimeout         = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target) && !triggerRef?.current?.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  const fetchGifs = useCallback(async (q) => {
    if (!GIPHY_KEY) {
      setError("Add REACT_APP_GIPHY_API_KEY to frontend/.env");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const endpoint = q
        ? `https://api.giphy.com/v1/gifs/search?api_key=${GIPHY_KEY}&q=${encodeURIComponent(q)}&limit=${LIMIT}&rating=g`
        : `https://api.giphy.com/v1/gifs/trending?api_key=${GIPHY_KEY}&limit=${LIMIT}&rating=g`;

      const res  = await fetch(endpoint);
      const data = await res.json();

      if (data.data) {
        setGifs(data.data);
      } else {
        setError("No GIFs found");
      }
    } catch {
      setError("Failed to load GIFs");
    } finally {
      setLoading(false);
    }
  }, []);

  // Load trending on mount
  useEffect(() => { fetchGifs(""); }, [fetchGifs]);

  const handleSearch = (val) => {
    setQuery(val);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => fetchGifs(val.trim()), 500);
  };

  const handleSelect = (gif) => {
    const url = gif.images?.fixed_height?.url || gif.images?.original?.url;
    onSelect(url);
    onClose();
  };

  return (
    <div className={styles.picker} ref={containerRef}>
      <div className={styles.header}>
        <div className={styles.searchBar}>
          <FiSearch size={14} className={styles.searchIcon} />
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search GIFs..."
            value={query}
            onChange={(e) => handleSearch(e.target.value)}
            autoFocus
          />
          {query && (
            <button className={styles.clearBtn} onClick={() => handleSearch("")}>
              <FiX size={13} />
            </button>
          )}
        </div>
        <div className={styles.poweredBy}>
          <span>Powered by GIPHY</span>
        </div>
      </div>

      <div className={styles.grid}>
        {loading ? (
          // Skeleton placeholders that match column layout
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className={styles.gifItem}
              style={{ height: i % 2 === 0 ? 100 : 130, background: "var(--border)" }} />
          ))
        ) : error ? (
          <div className={styles.error}>{error}</div>
        ) : gifs.length === 0 ? (
          <div className={styles.empty}>No GIFs found. Try searching something!</div>
        ) : (
          gifs.map((gif) => {
            // Pick smallest image that's still decent quality
            const src = gif.images?.fixed_width_small?.url
              || gif.images?.fixed_width?.url
              || gif.images?.fixed_height_small?.url
              || gif.images?.original?.url;
            if (!src) return null;
            return (
              <div key={gif.id} className={styles.gifItem} onClick={() => handleSelect(gif)}>
                <img src={src} alt={gif.title || "gif"} className={styles.gifImg} loading="lazy" />
              </div>
            );
          })
        )}
      </div>

      {!GIPHY_KEY && (
        <div className={styles.noKey}>
          ⚙️ Add <code>REACT_APP_GIPHY_API_KEY</code> to frontend/.env
        </div>
      )}
    </div>
  );
};

export default GifPicker;
